import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaPaperclip,
  FaEnvelope,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import {
  ReceiptIndianRupee,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const TCSReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "";

  const branchType =
    localStorage.getItem(
      "branchType"
    ) || "";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          branchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        branchType,
        userId,
      ]
    );

  /* =========================================================
     DATE
  ========================================================= */

  const today =
    new Date()
      .toISOString()
      .split("T")[0];

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId ||
      "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState(
    today
  );

  const [
    toDate,
    setToDate,
  ] = useState(
    today
  );

  const [
    taxType,
    setTaxType,
  ] = useState(
    "RD"
  );

  /* =========================================================
     DATA
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const [
    rowsPerPage,
    setRowsPerPage,
  ] = useState(10);

  /* =========================================================
     TEMPLATE CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD BRANCHES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/branches_tcsRpt",
        {
          params: {
            branchtype:
              branchType ||
              "HO",

            branchid:
              storedBranchId ||
              8,
          },

          headers:
            commonHeaders,
        }
      )
      .then(
        (res) => {
          const branches =
            res.data
              ?.ddl_Branch
              ?.items ||
            [];

          setBranchList(
            branches
          );

          if (
            branches.length >
            0
          ) {
            setBranchId(
              String(
                branches[0]
                  .fld_Branchid ||
                  storedBranchId ||
                  8
              )
            );
          }
        }
      )
      .catch(
        (err) => {
          console.error(
            "Branch fetch failed",
            err
          );

          toast.error(
            "Failed to load branch list"
          );
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchType,
    storedBranchId,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    () => {
      if (
        !fromDate ||
        !toDate ||
        !taxType
      ) {
        toast.warning(
          "Please select From Date, To Date and Tax Type"
        );

        return;
      }

      setLoading(
        true
      );

      axios
        .get(
          "https://nissi-india.com/api/tcsReportForListing",
          {
            params: {
              fromdate:
                fromDate,

              todate:
                toDate,

              branchId,

              taxType:
                taxType.toLowerCase(),
            },

            headers:
              commonHeaders,
          }
        )
        .then(
          (res) => {
            const rows =
              res.data
                ?.data ||
              [];

            setData(
              rows
            );

            setCurrentPage(
              1
            );

            toast.success(
              `Loaded ${rows.length} records`
            );
          }
        )
        .catch(
          (err) => {
            console.error(
              "TCS report fetch failed",
              err
            );

            toast.error(
              "Failed to fetch TCS report"
            );

            setData([]);
          }
        )
        .finally(() => {
          setLoading(false);
        });
    };

  /* =========================================================
     EXPORT
     BOTH ORIGINAL EXPORTS PRESERVED
  ========================================================= */

  const handleExport =
    async (
      detail = false
    ) => {
      if (
        !fromDate ||
        !toDate ||
        !taxType
      ) {
        toast.warning(
          "Please select From Date, To Date and Tax Type"
        );

        return;
      }

      const apiUrl =
        detail
          ? "https://nissi-india.com/api/tcsReportForListingExcelDet"
          : "https://nissi-india.com/api/tcsReportForListingExcel";

      try {
        const res =
          await axios.get(
            apiUrl,
            {
              params: {
                fromdate:
                  fromDate,

                todate:
                  toDate,

                branchId,

                taxType:
                  taxType.toLowerCase(),
              },

              headers:
                commonHeaders,

              responseType:
                "blob",
            }
          );

        const blob =
          new Blob(
            [
              res.data,
            ],
            {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
          );

        const url =
          window.URL.createObjectURL(
            blob
          );

        const a =
          document.createElement(
            "a"
          );

        a.href =
          url;

        a.download =
          detail
            ? `TCS_Report_Detail_${fromDate}_to_${toDate}.xlsx`
            : `TCS_Report_${fromDate}_to_${toDate}.xlsx`;

        document.body.appendChild(
          a
        );

        a.click();

        a.remove();

        window.URL.revokeObjectURL(
          url
        );

        toast.success(
          "Excel file downloaded successfully"
        );
      } catch (
        err
      ) {
        console.error(
          "Excel export failed",
          err
        );

        toast.error(
          "Failed to download Excel"
        );
      }
    };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint =
    () => {
      if (
        !fromDate ||
        !toDate ||
        !taxType
      ) {
        toast.warning(
          "Please select From Date, To Date and Tax Type"
        );

        return;
      }

      const query =
        new URLSearchParams(
          {
            fromDate,

            toDate,

            branchId,

            taxType:
              taxType.toLowerCase(),
          }
        ).toString();

      window.open(
        `/inventory/TCSReport_AllPrint?${query}`,
        "_blank"
      );
    };

  /* =========================================================
     VIEW INVOICE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewInvoice =
    (
      invoiceNo
    ) => {
      if (
        !invoiceNo
      ) {
        toast.warning(
          "Invoice number not available"
        );

        return;
      }

      axios
        .get(
          "https://nissi-india.com/api/grnInvoice_documentsRpt",
          {
            params: {
              invoiceno:
                invoiceNo,
            },

            headers:
              commonHeaders,
          }
        )
        .then(
          (res) => {
            const docs =
              res.data
                ?.data ||
              [];

            if (
              docs.length >
              0
            ) {
              docs.forEach(
                (doc) => {
                  if (
                    doc.url
                  ) {
                    window.open(
                      doc.url,
                      "_blank"
                    );
                  }
                }
              );
            } else {
              toast.warning(
                "No invoice document found"
              );
            }
          }
        )
        .catch(
          (err) => {
            console.error(
              "Invoice fetch failed",
              err
            );

            toast.error(
              "Failed to fetch invoice copy"
            );
          }
        );
    };

  /* =========================================================
     CLEAR
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setFromDate(
        today
      );

      setToDate(
        today
      );

      setTaxType(
        "RD"
      );

      setData([]);

      setCurrentPage(
        1
      );

      if (
        branchList.length >
        0
      ) {
        setBranchId(
          String(
            branchList[0]
              .fld_Branchid ||
              storedBranchId ||
              8
          )
        );
      } else {
        setBranchId(
          storedBranchId ||
            "8"
        );
      }
    };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency =
    (
      amount
    ) => {
      return new Intl.NumberFormat(
        "en-IN",
        {
          style:
            "currency",

          currency:
            "INR",

          minimumFractionDigits:
            2,

          maximumFractionDigits:
            2,
        }
      ).format(
        Number(
          amount ||
            0
        )
      );
    };

  /* =========================================================
     DATE
  ========================================================= */

  const formatDate =
    (
      value
    ) => {
      if (!value) {
        return "-";
      }

      const d =
        new Date(
          value
        );

      if (
        isNaN(d)
      ) {
        return value;
      }

      return d.toLocaleDateString(
        "en-GB"
      );
    };

  /* =========================================================
     STATUS COLOR
  ========================================================= */

  const getStatusColor =
    (
      status
    ) => {
      if (!status) {
        return isDark
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-100 text-gray-800";
      }

      const statusLower =
        String(
          status
        ).toLowerCase();

      if (
        statusLower.includes(
          "approve"
        ) ||
        statusLower.includes(
          "complete"
        )
      ) {
        return isDark
          ? "bg-green-900/30 text-green-300"
          : "bg-green-100 text-green-800";
      }

      if (
        statusLower.includes(
          "pending"
        ) ||
        statusLower.includes(
          "waiting"
        )
      ) {
        return isDark
          ? "bg-yellow-900/30 text-yellow-300"
          : "bg-yellow-100 text-yellow-800";
      }

      if (
        statusLower.includes(
          "reject"
        ) ||
        statusLower.includes(
          "cancel"
        )
      ) {
        return isDark
          ? "bg-red-900/30 text-red-300"
          : "bg-red-100 text-red-800";
      }

      return isDark
        ? "bg-blue-900/30 text-blue-300"
        : "bg-blue-100 text-blue-800";
    };

  /* =========================================================
     TOTALS
  ========================================================= */

  const totals =
    useMemo(
      () => {
        return data.reduce(
          (
            acc,
            row
          ) => {
            acc.accAmt +=
              Number(
                row.AccAmtNew ||
                  row.AccAmt ||
                  0
              );

            acc.sgst +=
              Number(
                row.SGSTAmountNew ||
                  row.SGSTAmount ||
                  0
              );

            acc.cgst +=
              Number(
                row.CGSTAmountNew ||
                  row.CGSTAmount ||
                  0
              );

            acc.igst +=
              Number(
                row.IGSTAmountNew ||
                  row.IGSTAmount ||
                  0
              );

            acc.total +=
              Number(
                row.TotalAmountNew ||
                  row.TotalAmount ||
                  0
              );

            return acc;
          },
          {
            accAmt: 0,
            sgst: 0,
            cgst: 0,
            igst: 0,
            total: 0,
          }
        );
      },
      [
        data,
      ]
    );

  /* =========================================================
     PAGINATION
  ========================================================= */

  const totalPages =
    Math.ceil(
      data.length /
        rowsPerPage
    );

  const paginatedData =
    useMemo(
      () => {
        const startIndex =
          (currentPage -
            1) *
          rowsPerPage;

        return data.slice(
          startIndex,
          startIndex +
            rowsPerPage
        );
      },
      [
        data,
        currentPage,
        rowsPerPage,
      ]
    );

  const startRecord =
    data.length ===
    0
      ? 0
      : (currentPage -
          1) *
          rowsPerPage +
        1;

  const endRecord =
    Math.min(
      currentPage *
        rowsPerPage,
      data.length
    );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            TCS Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <ReceiptIndianRupee className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  TCS Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Track and manage Tax Collected at Source records
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* NORMAL EXPORT */}

              <button
                type="button"
                onClick={() =>
                  handleExport(
                    false
                  )
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaFileExcel
                  size={14}
                />

                Export Excel
              </button>

              {/* FILTER TOGGLE */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* TAX TYPE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Tax Type{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <div
                    className={`flex flex-wrap gap-2 p-2.5 rounded-lg border ${
                      isDark
                        ? "bg-slate-800/60 border-slate-600/50"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    {[
                      "RD",
                      "URD",
                      "NoTax",
                    ].map(
                      (type) => (
                        <label
                          key={
                            type
                          }
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm font-medium transition ${
                            taxType ===
                            type
                              ? `bg-${baseColor}-${baseShade} text-white shadow`
                              : isDark
                              ? "text-slate-300 hover:bg-slate-700"
                              : "text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          <input
                            type="radio"
                            name="taxtype"
                            value={
                              type
                            }
                            checked={
                              taxType ===
                              type
                            }
                            onChange={(e) =>
                              setTaxType(
                                e.target.value
                              )
                            }
                            className="sr-only"
                          />

                          {type}
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* =============================================
                  ACTIONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* DETAILED EXCEL */}

                <button
                  type="button"
                  onClick={() =>
                    handleExport(
                      true
                    )
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-purple-900/30 text-purple-300 hover:bg-purple-900/50"
                      : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                  }`}
                >
                  <FaFileExcel
                    size={14}
                  />

                  Detailed Excel
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* RESULTS HEADER */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  TCS Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length >
                0 && (
                <div className="flex flex-wrap items-center gap-4">

                  <div
                    className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                      isDark
                        ? "bg-green-900/30 text-green-300"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    Total Amount:{" "}
                    {formatCurrency(
                      totals.total
                    )}
                  </div>

                  <div className="flex items-center gap-2">

                    <label
                      className={`text-sm ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-500"
                      }`}
                    >
                      Rows:
                    </label>

                    <select
                      value={
                        rowsPerPage
                      }
                      onChange={(e) => {
                        setRowsPerPage(
                          Number(
                            e.target.value
                          )
                        );

                        setCurrentPage(
                          1
                        );
                      }}
                      className={`px-3 py-2 border rounded-lg text-sm ${
                        isDark
                          ? "bg-slate-800 border-slate-600 text-white"
                          : "bg-white border-slate-300 text-slate-800"
                      }`}
                    >
                      <option value={10}>
                        10
                      </option>

                      <option value={25}>
                        25
                      </option>

                      <option value={50}>
                        50
                      </option>

                      <option value={100}>
                        100
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* LOADING */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading TCS records...
              </p>
            </div>
          ) : data.length >
            0 ? (
            <>
              {/* TABLE */}

              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        S.No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        GRN No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Supplier
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        GST No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Invoice No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        SGST
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        CGST
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        IGST
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Total
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Status
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >
                    {paginatedData.map(
                      (
                        row,
                        idx
                      ) => {
                        const serialNo =
                          (currentPage -
                            1) *
                            rowsPerPage +
                          idx +
                          1;

                        return (
                          <tr
                            key={
                              row.fld_billid ||
                              row.fld_GRNno ||
                              serialNo
                            }
                            className={`transition ${
                              isDark
                                ? "bg-gray-800/20 hover:bg-gray-700/30"
                                : "bg-white hover:bg-gray-50"
                            }`}
                          >

                            <td className="px-4 py-3 font-medium">
                              {serialNo}
                            </td>

                            {/* GRN */}

                            <td className="px-4 py-3">

                              <span
                                className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  isDark
                                    ? "bg-gray-700 text-gray-200"
                                    : `bg-${baseColor}-100 text-${baseColor}-800`
                                }`}
                              >
                                {row.fld_GRNno ||
                                  "-"}
                              </span>
                            </td>

                            {/* SUPPLIER */}

                            <td className="px-4 py-3 font-medium">
                              {row.fld_Suppliername ||
                                row.SupplierName ||
                                "-"}
                            </td>

                            {/* GST */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {row.fld_TIN ||
                                "-"}
                            </td>

                            {/* INVOICE */}

                            <td className="px-4 py-3">

                              <span
                                className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  isDark
                                    ? "bg-green-900/30 text-green-300"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {row.fld_invno ||
                                  "-"}
                              </span>
                            </td>

                            {/* DATE */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatDate(
                                row.BillDate
                              )}
                            </td>

                            {/* AMOUNT */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatCurrency(
                                row.AccAmtNew ||
                                  row.AccAmt
                              )}
                            </td>

                            {/* SGST */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatCurrency(
                                row.SGSTAmountNew ||
                                  row.SGSTAmount
                              )}
                            </td>

                            {/* CGST */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatCurrency(
                                row.CGSTAmountNew ||
                                  row.CGSTAmount
                              )}
                            </td>

                            {/* IGST */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              {formatCurrency(
                                row.IGSTAmountNew ||
                                  row.IGSTAmount
                              )}
                            </td>

                            {/* TOTAL */}

                            <td
                              className={`px-4 py-3 whitespace-nowrap font-semibold ${
                                isDark
                                  ? "text-green-300"
                                  : "text-green-700"
                              }`}
                            >
                              {formatCurrency(
                                row.TotalAmountNew ||
                                  row.TotalAmount
                              )}
                            </td>

                            {/* STATUS */}

                            <td className="px-4 py-3 whitespace-nowrap">

                              <span
                                className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                  row.fld_status
                                )}`}
                              >
                                {row.fld_status ||
                                  "Pending"}
                              </span>
                            </td>

                            {/* ACTIONS */}

                            <td className="px-4 py-3">

                              <div className="flex items-center justify-center gap-2">

                                {/* VIEW */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleViewInvoice(
                                      row.fld_invno
                                    )
                                  }
                                  className={`p-1.5 rounded-full transition ${
                                    isDark
                                      ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                      : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                                  }`}
                                  title="View Invoice"
                                >
                                  <FaPaperclip className="w-4 h-4" />
                                </button>

                                {/* MAIL */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    toast.info(
                                      `Send mail for ${row.fld_invno}`
                                    )
                                  }
                                  className={`p-1.5 rounded-full transition ${
                                    isDark
                                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                      : "bg-green-100 text-green-600 hover:bg-green-200"
                                  }`}
                                  title="Send Mail"
                                >
                                  <FaEnvelope className="w-4 h-4" />
                                </button>

                                {/* VERIFY */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    toast.success(
                                      `Checked record ${row.fld_invno || ""}`
                                    )
                                  }
                                  className={`p-1.5 rounded-full transition ${
                                    isDark
                                      ? "bg-purple-900/30 text-purple-300 hover:bg-purple-900/50"
                                      : "bg-purple-100 text-purple-600 hover:bg-purple-200"
                                  }`}
                                  title="Verify"
                                >
                                  <FaCheckCircle className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>

                  {/* TOTALS */}

                  <tfoot
                    className={
                      isDark
                        ? "bg-slate-900/70"
                        : "bg-slate-100"
                    }
                  >
                    <tr>

                      <td
                        colSpan="6"
                        className={`px-4 py-3 text-right text-sm font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Totals
                      </td>

                      <td className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(
                          totals.accAmt
                        )}
                      </td>

                      <td className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(
                          totals.sgst
                        )}
                      </td>

                      <td className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(
                          totals.cgst
                        )}
                      </td>

                      <td className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(
                          totals.igst
                        )}
                      </td>

                      <td
                        className={`px-4 py-3 text-sm font-bold ${
                          isDark
                            ? "text-green-300"
                            : "text-green-700"
                        }`}
                      >
                        {formatCurrency(
                          totals.total
                        )}
                      </td>

                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* PAGINATION */}

              <div
                className={`p-6 border-t ${
                  isDark
                    ? "border-slate-700"
                    : "border-slate-200"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Showing{" "}
                    <span className="font-semibold">
                      {startRecord}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {endRecord}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {data.length}
                    </span>{" "}
                    entries
                  </div>

                  <div className="flex items-center gap-2">

                    {/* PREVIOUS */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (
                            prev
                          ) =>
                            Math.max(
                              prev -
                                1,
                              1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                        1
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage ===
                        1
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <FaChevronLeft
                        size={12}
                      />

                      Prev
                    </button>

                    {/* PAGE */}

                    <span
                      className={`px-4 py-2 text-sm font-semibold rounded-lg bg-${baseColor}-${baseShade} text-white`}
                    >
                      Page{" "}
                      {currentPage}{" "}
                      of{" "}
                      {totalPages ||
                        1}
                    </span>

                    {/* NEXT */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (
                            prev
                          ) =>
                            Math.min(
                              prev +
                                1,
                              totalPages ||
                                1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                          totalPages ||
                        totalPages ===
                          0
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage ===
                          totalPages ||
                        totalPages ===
                          0
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Next

                      <FaChevronRight
                        size={12}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (

            /* EMPTY */

            <div className="px-4 py-14 text-center">

              <FaCheckCircle
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No TCS records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default TCSReport;